import { t } from "i18next";
import mapboxgl, { FitBoundsOptions, GeoJSONSource, LngLat, LngLatBounds, Map, Popup } from "mapbox-gl";
import Bubble from "../rsc/ic_bubble_price.png";
import DOIcon from "../rsc/ic_do.png";
import Middleman from "../rsc/ic_middleman.png";
import MyCompany from "../rsc/ic_my_company.png";
import Pickup from "../rsc/ic_pickup.png";
import Mill from "../rsc/ic_mill.svg";
import RampIcon from "../rsc/ic_ramp.png";
import { Harvest } from "./contract/Harvest";
import { MapEvent } from "./MapEvent";
import { Organisation } from "./organisation/Organisation";
import { PriceRadius } from "./price/PriceRadius";
import { MillPriceOnMap } from "./price/MillPriceOnMap";
import { colorApple050, colorBrown200, colorGold600, colorGreen300, colorGreen500, colorLight } from "../rsc/colors/colors";

function overlayImageMyCompany(): Promise<string> {
    var image1 = new Image();
    image1.src = MyCompany;
    return new Promise<string>((resolve) => {
        image1.onload = function () {
            var image2 = new Image();
            image2.src = DOIcon;
            image2.onload = function () {
                var canvas = document.createElement('canvas');
                var ctx = canvas.getContext('2d');
                canvas.width = image1.width;
                canvas.height = image1.height;
                ctx.drawImage(image1, 0, 0);
                const img2Width = image2.width / 3
                const img2Height = image2.height / 3
                ctx.drawImage(image2, (image1.width / 2) - (img2Width / 2), 18, img2Width, img2Height);
                resolve(canvas.toDataURL())
            }
        }
    })
}

function overlayImagePrice(img: any, scale: number, right = 20, top = 15): Promise<string> {
    var image1 = new Image();
    image1.src = Bubble;
    return new Promise((resolver) => {
        image1.onload = function () {
            var image2 = new Image();
            image2.src = img;
            image2.onload = function () {
                var canvas = document.createElement('canvas');
                var ctx = canvas.getContext('2d');
                canvas.width = image1.width;
                canvas.height = image1.height;
                ctx.drawImage(image1, 0, 0);
                const img2Width = image2.width / scale
                const img2Height = image2.height / scale
                ctx.drawImage(image2, right, top, img2Width, img2Height);
                resolver(canvas.toDataURL())
            }
        }
    })
}

export function buildDataForPricePage(map: Map): Promise<boolean> {
    return addMyCompaniesFeature(map)
}

function getPolygonMill(coords: number[][]): any {
    return {
        "type": "FeatureCollection",
        "features": [{
            "type": "Feature",
            "geometry": {
                "type": "Polygon",
                "coordinates": [coords]
            },
            "properties": {
            }
        }]
    }
}

export function updatePolygonMill(map: Map, coords: number[][]) {
    const json = getPolygonMill(coords);
    (map.getSource("mill_polygon") as GeoJSONSource).setData(json);
}

export function addPriceCirclRadius(map: Map, organisation: Organisation) {
    if (map.getLayer("priceCircle")) {
        map.removeLayer("priceCircle")
        map.removeSource("priceCircle")
    }
    let points = 64;

    var coords = {
        latitude: organisation.positionepsg4326.coordinates[1],
        longitude: organisation.positionepsg4326.coordinates[0]
    };

    var km = 10;

    var ret = [];
    var distanceX = km / (111.320 * Math.cos(coords.latitude * Math.PI / 180));
    var distanceY = km / 110.574;

    var theta, x, y;
    for (var i = 0; i < points; i++) {
        theta = (i / points) * (2 * Math.PI);
        x = distanceX * Math.cos(theta);
        y = distanceY * Math.sin(theta);

        ret.push([coords.longitude + x, coords.latitude + y]);
    }
    ret.push(ret[0]);

    map.addSource("priceCircle", {
        "type": "geojson",
        "data": {
            "type": "FeatureCollection",
            "features": [{
                "type": "Feature",
                "geometry": {
                    "type": "Polygon",
                    "coordinates": [ret]
                },
                "properties": {
                }
            }]
        }
    })

    map.addLayer({
        "id": "priceCircle",
        "type": "fill",
        "source": "priceCircle",
        "layout": {},
        "paint": {
            "fill-color": "blue",
            "fill-opacity": 0.2
        }
    });
}

export function buildDataForMarketPage(map: Map, prices: PriceRadius[], organisation: Organisation) {
    if (map.getLayer('organisation')) {
        map.removeLayer('organisation')
        map.removeSource('organisation')
    }
    if (map.getLayer('prices')) {
        map.removeLayer('prices')
        map.removeSource('prices')
    }
    if (organisation) {
        addMyCompanyFeature(map, organisation)
    }
    if (prices) {
        addPricesFeatures(map, prices)
    }
}

export function buildDataForCompanyDetailPage(map: Map, organisation: Organisation, defaultOrgCoord: number[] = null): Promise<boolean> {
    return new Promise((resolver) => {
        addMyCompanyFeature(map, organisation, defaultOrgCoord).then((value) => {
            resolver(value)
        })
    })
}

export function addDragForCompanyDetailPage(map: Map, mapEvent: MapEvent) {
    map.on('mousedown', 'organisation', mapEvent.onDown)
    map.on('click', mapEvent.onMapClick)
}

export function removeCompanyDetailSource(map: Map) {
    if (map.getLayer('organisation')) {
        map.removeLayer('organisation')
        map.removeSource('organisation')
    }
}

export function removeDragForCompanyDetailPage(map: Map, mapEvent: MapEvent) {
    map.off("mousemove", mapEvent.onMove)
    map.off("mouseup", mapEvent.onUp)
    map.off('mousedown', 'organisation', mapEvent.onDown)
    map.off('click', mapEvent.onMapClick)
}

function loadPickupImage(map: Map, block: () => void) {
    if (map.hasImage('pickup') === false) {
        var img = new Image()
        img.src = Pickup
        img.onload = function () {
            map.addImage('pickup', img, {
                pixelRatio: 5
            })
            block()
        }
    } else {
        block()
    }
}

function loadPricesImage(map: Map, block: () => void) {
    if (map.hasImage('drop_off') === false) {
        overlayImagePrice(DOIcon, 3, undefined, 10)
            .then((src1) => {
                var img = new Image()
                img.src = src1
                img.onload = function () {
                    map.addImage('drop_off', img, {
                        pixelRatio: 1.5
                    })
                    overlayImagePrice(RampIcon, 2, 17)
                        .then((src2) => {
                            const img2 = new Image()
                            img2.src = src2
                            img2.onload = function () {
                                map.addImage('loading_ramp', img2, {
                                    pixelRatio: 1.5
                                })
                                overlayImagePrice(Middleman, 1.5, 17, 4)
                                    .then((src3) => {
                                        const img3 = new Image()
                                        img3.src = src3
                                        img3.onload = function () {
                                            map.addImage('freelance', img3, {
                                                pixelRatio: 1.5
                                            })
                                            block()
                                        }
                                    })
                            }
                        })
                }
            })
    } else {
        block()
    }
}

function loadCompanyImage(map: Map, block: (src?: string) => void) {
    if (map.hasImage('my_company') === false) {
        overlayImageMyCompany().then((src) => {
            var img = new Image()
            img.src = src
            img.onload = function () {
                map.addImage('my_company', img, {
                    pixelRatio: 1.5
                })
                block(src)
            }
        })
    } else {
        block()
    }
}

function addPricesFeatures(map: Map, prices: PriceRadius[]) {
    var source = []
    loadPricesImage(map, () => {
        prices.forEach((price: any) => {
            var position = price.organisation.positionepsg4326.coordinates
            var imgPriceOrgType = price.organisation.organisation_type
            source.push({
                "type": 'Feature',
                "geometry": {
                    'type': 'Point',
                    'coordinates': [position[0], position[1]]
                },
                'properties': {
                    'icon': imgPriceOrgType,
                    'price': price.price.price_per_quantity_unit
                }
            })
        })
        map.addSource('prices', {
            "type": 'geojson',
            'data': {
                'type': 'FeatureCollection',
                'features': source
            }
        })

        map.addLayer({
            'id': 'prices',
            'type': 'symbol',
            'source': 'prices',
            'layout': {
                'icon-image': ['get', 'icon'],
                'text-field': ['get', 'price'],
                'text-offset': [1, -0.9],
                'text-anchor': 'top'
            }
        })
    })
}

function addMyCompanyFeature(map: Map, organisation: Organisation, defaultOrgCoord?: number[]): Promise<boolean> {
    return new Promise((resolver) => {
        let coordinates = organisation?.positionepsg4326?.coordinates ? organisation?.positionepsg4326?.coordinates : defaultOrgCoord ? defaultOrgCoord : null
        if (coordinates && !map.getSource('organisation')) {
            loadCompanyImage(map, () => {
                map.addSource('organisation', {
                    "type": 'geojson',
                    'data': getMyCompaniyDetailSource(coordinates, organisation.name)
                })
                map.addLayer({
                    'id': 'organisation',
                    'type': 'symbol',
                    'source': 'organisation',
                    'layout': {
                        'icon-image': 'my_company',
                        "icon-anchor": 'bottom'
                    }
                })
                resolver(true)
            })
        } else {
            resolver(false)
        }
    })
}

export function updatePopup(map: Map, price: any, priceSelected?: MillPriceOnMap, popup?: any) {
    popup.current?.remove()
    buildPopup(map, price, priceSelected, popup)
}

function backgroundColorQBP(position: number): string {
    switch (position) {
        case 0: return colorGreen500;
        case 1: return colorGreen300;
        case 2: return colorApple050;
        case 3: return colorGold600;
        case 4: return colorBrown200;
    }
    return colorLight();
}


function buildPopup(map: Map, price: any, priceSelected?: MillPriceOnMap, popup?: any) {
    const oName = price?.properties.displayName?.() ?? price?.properties.mill_full_name
    const coord = price?.geometry.coordinates
    let htmlPrice = ""
    price?.properties?.tiers?.forEach((p, index) => {
        const priceToShow = p.price_pempem ? `${p.price_pempem}` : t('no_price_set');
        const color = backgroundColorQBP(index)
        const nameToShow = p.name ?? t('no_name');
        htmlPrice += `
            <div class="pempem-flex-row gap10">
                <span class="bold" style="color:#000; background-color: ${color}; min-width:100px; padding: 2px 5px;">${nameToShow}</span>
                <div>
                    <span class="bold">Rp/Kg <span style="font-weight:bold;font-size:20px;">${priceToShow.IndoFormat()}</span></span>
                </div>                
            </div>
        `;
    })
    const pricePerUnit = price?.properties?.price_per_kg || 0;
    const standardGradingRatio = price?.properties?.standard_grading_ratio || 0;
    const discountAmount = (pricePerUnit * standardGradingRatio) / 100;
    const finalPrice = pricePerUnit - discountAmount;

    const html = `
    <div style="
            background: #fff;
            border-radius: 10px;
            box-shadow: 0 2px 12px rgba(0,0,0,0.13);
            text-align: center;
        ">
            <div class="pempem-flex-row-center" style="
                padding: 14px 18px 0px 18px;
                font-size: 1.1em;
                font-weight: bold;
                color: #2a4d8f;
                letter-spacing: 0.5px;
                margin-bottom: 8px;
            ">
                <img src="${Mill}" alt="My Icon" style="width: 20px; height: 20px; margin-right: 5px;" />
                ${oName ?? ''}
            </div>

            <div class="dotted-line"></div>

            <div class="pempem-flex-row-center gap5" style="padding: 6px 6px; font-size: 0.9em; color: #555;">
                <div style="font-size:12px">
                    ${pricePerUnit} - ${standardGradingRatio}% (${(pricePerUnit * (standardGradingRatio / 100)).toFixed(1)}) = 
                </div>
                <b style="font-size:20px">${finalPrice.IndoFormat()}</b>
            </div>

            <div class="dotted-line" style="display: ${htmlPrice ? "block" : "none"};"></div>

            ${htmlPrice &&
        `<div class="pempem-flex-col gap5" style="
                  padding: 14px 10px 10px 18px;">
                 ${htmlPrice}
            </div>`
        }
        </div>
        `
    if (coord) {
        popup.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 60, anchor: "bottom" }).setLngLat([coord[0], coord[1]]).setHTML(html)
        popup.current.addTo(map)
    }
}

function addMyCompaniesFeature(map: Map): Promise<boolean> {
    return new Promise((r, e) => {
        loadCompanyImage(map, () => {
            let source = []
            // prices.forEach(p => {
            //     const o = p.org
            //     const coord = o?.positionepsg4326?.coordinates
            //     if (coord) {
            //         source.push({
            //             "type": 'Feature',
            //             "geometry": {
            //                 'type': 'Point',
            //                 'coordinates': [coord[0], coord[1]]
            //             },
            //             'properties': {
            //                 'icon': "my_company",
            //                 'org_id': o.id
            //             }
            //         })
            //     }
            // })
            // buildPopup(map, prices, popups)
            map.addSource('organisation', {
                "type": 'geojson',
                'data': {
                    'type': 'FeatureCollection',
                    'features': source
                }
            })
            map.addSource('mill_polygon', {
                "type": 'geojson',
                'data': getPolygonMill([])
            })
            map.addLayer({
                'id': 'mill_polygon',
                'type': 'fill',
                'source': 'mill_polygon', // reference the data source
                'layout': {},
                'paint': {
                    'fill-color': '#0080ff', // blue color fill
                    'fill-opacity': 0.5
                }
            });
            // Add a black outline around the polygon.
            map.addLayer({
                'id': 'mill_polygon_line',
                'type': 'line',
                'source': 'mill_polygon',
                'layout': {},
                'paint': {
                    'line-color': '#000',
                    'line-width': 3
                }
            });
            // map.addLayer({
            //     'id': 'organisation',
            //     'type': 'symbol',
            //     'source': 'organisation',
            //     'layout': {
            //         'icon-image': ['get', 'icon'],
            //         "icon-anchor": 'bottom',
            //         "icon-allow-overlap": true,
            //     }
            // })

            // map.on('click', 'organisation', (e) => {
            //     // Get the clicked feature
            //     const properties = e.features[0].properties;

            //     onClick?.(properties.org_id)
            // });
            // map.on('mouseenter', 'organisation', () => {
            //     map.getCanvas().style.cursor = 'pointer';
            // });

            // // Change the cursor back to default when the mouse leaves the symbol layer
            // map.on('mouseleave', 'organisation', () => {
            //     map.getCanvas().style.cursor = '';
            // });
            r(true)
        })
    })
}

export function getMyCompaniyDetailSource(defaultOrgCoord: number[], name: string): any {
    return {
        'type': 'FeatureCollection',
        'features': [{
            "type": 'Feature',
            "geometry": {
                'type': 'Point',
                'coordinates': [defaultOrgCoord[0], defaultOrgCoord[1]]
            },
            'properties': {
                'title': name
            }
        }]
    }
}

export function addContractDetailLayers(map: Map, popups: Popup[]) {
    loadPickupImage(map, () => {
    })
    map.addSource('pickups', {
        'type': 'geojson',
        'data': getPickedUpSource([])
    });

    map.addLayer({
        'id': 'pickups',
        'type': 'symbol',
        'source': 'pickups',
        'layout': {
            'icon-image': 'pickup',
            "icon-anchor": 'bottom',
            "icon-allow-overlap": true
        }
    })

    map.addSource('landplot', {
        'type': 'geojson',
        'data': getHarvestsSource([])
    });

    map.addLayer({
        'id': 'landplot',
        'type': 'fill',
        'source': 'landplot', // reference the data source
        'layout': {},
        'paint': {
            'fill-color': '#0080ff', // blue color fill
            'fill-opacity': 0.5
        }
    });
    // Add a black outline around the polygon.
    map.addLayer({
        'id': 'outline',
        'type': 'line',
        'source': 'landplot',
        'layout': {},
        'paint': {
            'line-color': '#000',
            'line-width': 3
        }
    });

    map.on('click', 'landplot', e => {
        const html = `<p style="padding:10px;">${e.features[0].properties.quantity} KG</p>`
        const popup = new mapboxgl.Popup({ closeButton: false })
            .setLngLat(e.lngLat)
            .setHTML(html)
        popups.push(popup)
        popup.addTo(map)
    })
}

export function updatePlots(map: Map, harvests: Harvest[]) {
    if (map) {
        var json = getHarvestsSource(harvests);
        (map.getSource("landplot") as GeoJSONSource).setData(json);
    }
}


export function updatePickedUps(map: Map, pickedUp: number[][]) {
    if (map) {
        var json = getPickedUpSource(pickedUp);
        (map.getSource("pickups") as GeoJSONSource).setData(json);
    }
}


function getPickedUpSource(pickedUp: number[][]): any {
    let pickups = []
    pickedUp.forEach((pos: number[]) => {
        pickups.push({
            'type': 'Feature',
            'properties': {
            },
            'geometry': {
                'type': 'Point',
                'coordinates': pos
            }
        })
    })
    return {
        'type': 'FeatureCollection',
        'features': pickups
    }
}


function getHarvestsSource(harvests: Harvest[]): any {
    let h = []
    harvests?.filter(h => h.land_plot?.polygon).forEach((harvest: Harvest) => {
        h.push({
            'type': 'Feature',
            'properties': {
                'quantity': harvest.quantity
            },
            'geometry': {
                'type': 'Polygon',
                'coordinates': harvest.land_plot.polygon.coordinates
            }
        })
    })
    return {
        'type': 'FeatureCollection',
        'features': h
    }
}

export function zoomForCoordinates(map: Map, coords: LngLat[], maxZoom: number = undefined, duration: number = undefined) {
    if (map && coords?.length > 0) {
        const bounds = new LngLatBounds(
            coords[0],
            coords[0]
        );
        for (const c of coords) {
            bounds.extend(c);
        }
        var options: FitBoundsOptions = {
            padding: 20,
            duration: 0
        }
        if (maxZoom) {
            options = {
                ...options,
                maxZoom: maxZoom
            }
        }
        if (duration) {
            options = {
                ...options,
                duration: duration
            }
        }

        map.fitBounds(bounds, options)
        map.resize();
    }
}